// utils/poolaPaths.js
//
// The Poola page lives at two addresses — /poola/ for the sellers club and
// pending members, /suppliers/poola/ for suppliers (so the route resolves to the
// suppliers club; see routes.js). The last-Poola page mirrors both. The Poola
// page needs the address of its last-Poola twin for the recap card's link, and
// the last-Poola page needs the address of the Poola page it goes back to. One
// place decides the pair from the current route, so the two can't drift.
import { CLUB_TYPES, clubTypeForPath, SUPPLIER_STAGES, supplierStageOf } from '@/clubs/manifest.js';

const SELLERS = Object.freeze({ poola: '/poola/', last: '/poola/last/' });
const SUPPLIERS = Object.freeze({ poola: '/suppliers/poola/', last: '/suppliers/poola/last/' });

/**
 * The Poola page and its last-Poola page for the namespace `routePath` is in.
 *
 * @param {string} [routePath] - the current F7 route path, e.g. '/suppliers/poola/'.
 * @returns {{ poola: string, last: string }}
 */
export function poolaPathsFor(routePath) {
  return clubTypeForPath(routePath) === CLUB_TYPES.SUPPLIERS ? SUPPLIERS : SELLERS;
}

/**
 * The Poola page a user should open from a screen that is not inside either
 * shell (the waiting screen, the club choice): anyone supplier-side — any stage
 * but `none` — gets the supplier address, everyone else the seller one.
 *
 * @param {{ supplierStage?: string, isSupplier?: boolean }} [who]
 * @returns {string} '/poola/' or '/suppliers/poola/'.
 */
export function poolaPathForStage(who = {}) {
  return supplierStageOf(who) === SUPPLIER_STAGES.NONE ? SELLERS.poola : SUPPLIERS.poola;
}

// Both slash forms of the two seller Poola pages, mapped to their supplier twins.
const SELLER_TO_SUPPLIER = Object.freeze({
  '/poola/': SUPPLIERS.poola,
  '/poola': SUPPLIERS.poola,
  '/poola/last/': SUPPLIERS.last,
  '/poola/last': SUPPLIERS.last,
});

/**
 * Where a seller Poola address sends someone supplier-side (supplier-showroom
 * P2-A): any stage but `none` on /poola/ or /poola/last/ → the supplier twin,
 * so the page resolves to the suppliers club (no seller tabbar, no seller
 * deposit banner). Null when no rewrite is due — a seller, or another path.
 * Shared by the warm guard (routes.js) and the cold pre-empt (App.vue).
 *
 * @param {string} path - the requested path, e.g. '/poola/'.
 * @param {{ supplierStage?: string, isSupplier?: boolean }} [who]
 * @returns {string|null}
 */
export function supplierPoolaTwin(path, who = {}) {
  if (supplierStageOf(who) === SUPPLIER_STAGES.NONE) return null;
  return SELLER_TO_SUPPLIER[path] ?? null;
}
